import { useMemo } from 'react';
import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  type AutocompleteProps,
  createFilterOptions,
  TextField,
} from '@mui/material';
import { AutocompleteOptionWithHint } from '@shared/ui';
import {
  ControlWrapper,
  getDefaultInputPropsAutocomplete,
  getAutocompleteOnChange,
  getAutocompleteValue,
  withControl,
} from '@unicorn/control';
import { useAutoSelectSingleOption } from '../../lib/use-auto-select-single-option';
import { ResidentAccountsControlGate, residentAccountsQuery } from './model';
import type { ResidentAccount, Props } from './types';

const filterOptions = createFilterOptions<ResidentAccount>({
  stringify: ({ label, type, company, inn }) =>
    [label, type, company, inn].filter(Boolean).join(' '),
});

export const ResidentAccountsControl = withControl<
  Partial<AutocompleteProps<ResidentAccount, boolean, boolean, boolean>> & Props
>(
  ({
    label,
    value,
    setValue,
    error,
    isError,
    required,
    readOnly,
    disabled,
    helperText,
    multiple,
    clearable,
    placeholder,
    residentGuid,
    areaGuids = [],
    autoSelectSingleOption = false,
    ...props
  }) => {
    const memoAreaGuids = useMemo(() => areaGuids, [areaGuids.join(',')]);

    useGate(ResidentAccountsControlGate, { residentGuid, areaGuids: memoAreaGuids });
    const { data, pending } = useUnit(residentAccountsQuery);

    const options = Array.isArray(data) ? data : [];

    const setValueFromControl = useAutoSelectSingleOption({
      enabled:
        autoSelectSingleOption && Boolean(residentGuid) && memoAreaGuids.length > 0,
      loading: pending,
      multiple,
      options,
      setValue,
      value,
    });

    const handleChange = (
      _: unknown,
      newValue: string | ResidentAccount | (string | ResidentAccount)[] | null
    ) => getAutocompleteOnChange(newValue, setValueFromControl, multiple);

    return (
      <ControlWrapper
        label={label}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        isError={isError}
        error={error}
        helperText={helperText}
      >
        <Autocomplete
          value={getAutocompleteValue(options, value, multiple)}
          onChange={handleChange}
          options={options}
          multiple={multiple}
          loading={pending}
          disableClearable={!clearable}
          filterOptions={filterOptions}
          renderInput={(params) => {
            const slotProps = {
              input: getDefaultInputPropsAutocomplete({
                params,
                readOnly,
                disabled,
                pending,
              }),
            };

            return (
              <TextField
                {...params}
                error={isError}
                placeholder={
                  multiple && Array.isArray(value) && value.length > 0 ? '' : placeholder
                }
                slotProps={slotProps}
              />
            );
          }}
          renderOption={({ key, ...props }, option) => {
            const optionDescription = [option.type, option.company, option.inn]
              .filter(Boolean)
              .join(', ');

            return (
              <li key={key} {...props}>
                <AutocompleteOptionWithHint
                  label={option.label}
                  hint={optionDescription}
                />
              </li>
            );
          }}
          readOnly={readOnly}
          disabled={disabled}
          {...props}
        />
      </ControlWrapper>
    );
  }
);
